import React, { useState } from 'react';
import axios from 'axios';
import { Phone, CheckCircle2, Loader2 } from 'lucide-react';
import { usePlatformContact } from '../../hooks/usePlatformContact';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPLOYEE_RANGES = ['1–10', '11–50', '51–100', '101–250', '251–500', '500+'];

const DemoRequestSection = () => {
  const { phone: supportPhone, phoneHref: supportTel, hours: supportHours } = usePlatformContact();
  const [form, setForm] = useState({
    fullName: '', companyName: '', phone: '', email: '',
    employeeRange: '', notes: '',
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.fullName || !form.companyName || !form.phone) {
      setError('Please provide your name, company, and phone.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/demo-requests`, { ...form, source: 'landing_page' });
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || `Something went wrong. Please call us at ${supportPhone}.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="request-demo" className="py-8 lg:py-12 bg-cream-100" data-testid="demo-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Legacy anchor — old links used #demo, keep both live so nothing silently breaks. */}
        <a id="demo" className="block relative -top-24" aria-hidden="true" />
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          {/* Left: copy */}
          <div className="lg:col-span-5 space-y-6">
            <p className="text-sm font-semibold uppercase tracking-widest text-gold-500">Get Started</p>
            <h2 className="font-display text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight">
              Request a Demo or Call Me Back
            </h2>
            <p className="text-lg text-gray-600">
              Not ready to enroll yet? Let one of our employer health specialists walk you through
              the program, answer your questions, and build a custom plan for your workforce. No
              obligation, no pressure &mdash; just a 20-minute conversation.
            </p>
            <div className="flex items-center gap-3 p-5 bg-white rounded-2xl border border-gray-200">
              <div className="h-12 w-12 bg-red-50 text-red-600 rounded-xl flex items-center justify-center">
                <Phone className="h-5 w-5" />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">Prefer to talk now?</p>
                <a href={supportTel} className="font-display text-2xl font-bold text-gray-900 hover:text-red-600" data-testid="demo-callout-phone">
                  {supportPhone}
                </a>
                <p className="text-xs text-gray-500 mt-1">{supportHours}</p>
              </div>
            </div>
            <ul className="space-y-2 text-sm text-gray-600 pt-2">
              <li>✓ 20-minute no-obligation demo call</li>
              <li>✓ Custom plan recommendation for your employee count</li>
              <li>✓ Personalized ROI estimate based on your industry</li>
              <li>✓ Personal callback from an employer health specialist</li>
            </ul>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-7 bg-white rounded-2xl border border-gray-200 p-8 shadow-sm">
            <h3 className="font-display text-2xl font-bold text-gray-900 mb-1">Talk to an Employer Specialist</h3>
            <p className="text-sm text-gray-600 mb-6">Leave your details and a specialist will be in touch.</p>
            {done ? (
              <div className="text-center py-12" data-testid="demo-success">
                <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-green-100 text-green-600 mb-4">
                  <CheckCircle2 className="h-8 w-8" />
                </div>
                <h3 className="font-display text-2xl font-bold text-gray-900">Thanks — we&apos;ll be in touch.</h3>
                <p className="text-gray-600 mt-2">Our team will reach out within 2 business hours.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <Field label="Full Name *" name="fullName" value={form.fullName} onChange={handleChange} placeholder="Mark Thompson" />
                <Field label="Company *" name="companyName" value={form.companyName} onChange={handleChange} placeholder="Columbus Manufacturing Co." />
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Phone *" name="phone" value={form.phone} onChange={handleChange} placeholder="(614) 555-1234" />
                  <Field label="Work Email" name="email" value={form.email} onChange={handleChange} placeholder="mark@company.com" type="email" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Team Size</label>
                  <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                    {EMPLOYEE_RANGES.map((r) => (
                      <button
                        type="button"
                        key={r}
                        onClick={() => setForm((f) => ({ ...f, employeeRange: r }))}
                        data-testid={`team-range-${r}`}
                        className={`text-xs font-semibold py-2 rounded-lg border transition ${
                          form.employeeRange === r
                            ? 'bg-red-600 text-white border-red-600'
                            : 'bg-white text-gray-700 border-gray-200 hover:border-red-300'
                        }`}
                      >
                        {r}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Anything specific?</label>
                  <textarea
                    name="notes"
                    value={form.notes}
                    onChange={handleChange}
                    rows={3}
                    placeholder="Currently insured? Self-funded? Specific questions?"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500/30 focus:border-red-500"
                    data-testid="demo-notes"
                  />
                </div>

                {error && (
                  <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-2" data-testid="demo-error">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="demo-submit"
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl transition-colors shadow-lg shadow-red-600/20 disabled:opacity-60"
                >
                  {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : 'Request a Callback'}
                </button>
                <p className="text-xs text-gray-500 text-center">
                  By submitting, you agree to be contacted by a Hometown Health employer specialist. We never share your information.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, name, value, onChange, placeholder, type = 'text' }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      data-testid={`demo-${name}`}
      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500/30 focus:border-red-500"
    />
  </div>
);

export default DemoRequestSection;
